import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CountDown from '../../containers/countdown/CountDown'
import { countDownType, membershipNameType, PlanChangeLink } from '../../utils/constant'
import { isSunday } from '../../utils/utils'
import TrialPlanChoice from '../trial/TrialPlanChoice'
import WordsDiscoveryCallout from './WordsDiscoveryCallout'
import './storyFinishedTrial.css'
import { captureWeeklyEvent } from '../../utils/posthogWeekly'
import { isWeeklyCohortMember, isWeeklyHardGateActive } from '../../utils/weeklyOffer'

// Not copy: the lock shown instead of the finished picture.
const LOCK_ICON = '\u{1F512}'

interface IStoryFinishedProps {
  readAgain: any
}

const StoryFinished: React.FC<IStoryFinishedProps> = (props: any) => {
  const { membership, readAgain, trialEligibility, credentials, story } = props
  const { t } = useTranslation('dashboard')

  const isFree = membership?.name == membershipNameType.Free
  const isPremium = membership?.name == membershipNameType.Premium
  const isStandard = membership?.name == membershipNameType.Standard
  const isSchool = membership?.name == membershipNameType.School
  const isPro = membership?.name == membershipNameType.Pro
  const isTeacher = membership?.name == membershipNameType.Teacher
  const isWeekly = membership?.name == membershipNameType.Weekly
  // const isPaid = isPremium || isStandard || isSchool || isPro
  const isPaid = isPremium || isStandard || isSchool || isPro || isTeacher
  const isSundayToday = isSunday()
  const isTrialEligible = isFree && trialEligibility?.isEligible
  // Free learners in the Weekly cohort get the upgrade prompt instead of the
  // "next story on Sunday" message — they must pick a plan, not wait for Sunday.
  const isWeeklyOfferUser = isFree && isWeeklyCohortMember(credentials)
  const weeklyHardGate = isWeeklyHardGateActive()
  // A Free learner's own first story opens on any day (the API flags it `is_first_story`). Finishing
  // it on a weekday is the moment to offer the trial, not to show a lock they never hit.
  const isFirstStoryDone = isFree && !isWeeklyOfferUser && !isSundayToday && !!story?.is_first_story

  // U9: a Weekly subscriber finished a pinned story (reading-stickiness signal).
  useEffect(() => {
    if (isWeekly) {
      captureWeeklyEvent('weekly_story_completed')
    }
  }, [])

  return (
    <div className='section-2'>
      <div className='section-story w-container'>
        <div className='w-row section-story-finished'>
          {isPaid && (
            <div className='story-finished-countdown-meta'>
              <div className='section-countdown'>
                <div className='countdown-title'>{t('finished.nextLessonIn')} </div>
                <CountDown type={countDownType.Day} />
              </div>
            </div>
          )}
          <div className='w-col-12 icon-finished'>
            {isWeeklyOfferUser || (isFree && !isSundayToday && !isFirstStoryDone) ? (
              <div style={{ fontSize: '64px', color: '#666' }}>{LOCK_ICON}</div>
            ) : (
              <img src='/images/icons/icon-finished.jpg' alt='' />
            )}
          </div>
          <div className='w-col-12 story-finished-title'>
            {isWeeklyOfferUser ? (
              <span>{weeklyHardGate ? t('finished.freeAccessEnded') : t('finished.freeAccessEnding')}</span>
            ) : (
              isFree && (
                <span>
                  {isFirstStoryDone
                    ? t('finished.firstLessonDone')
                    : isSundayToday
                      ? t('finished.lessonComplete')
                      : t('finished.accessLocked')}
                </span>
              )
            )}
            {isPaid && <span>{t('finished.congratulation')}</span>}
          </div>
          {isPaid && (
            <div className='w-col-12 story-finished-upgrade'>
              <p>{t('finished.todayDone')}</p>
              <a className='button-4 w-inline-block' onClick={readAgain}>
                {t('finished.readAgain')}
              </a>
            </div>
          )}
          {isPaid && !isTeacher && <WordsDiscoveryCallout />}
          {isWeeklyOfferUser && (
            <div className='w-col-12 story-finished-upgrade'>
              <p>
                {weeklyHardGate ? t('finished.weeklyPaused') : t('finished.weeklyChoose')}
              </p>
              <a
                href={PlanChangeLink}
                className='button-4 w-inline-block button-normal'
                style={{ cursor: 'pointer' }}
              >
                {t('finished.seePlans')}
              </a>
            </div>
          )}
          {isFree && !isWeeklyOfferUser && (
            <>
              <div className='w-col-12 story-finished-countdown section-countdown'>
                <p>
                  {isFirstStoryDone || isSundayToday ? t('finished.nextFreeSunday') : t('finished.freeOnSundays')}
                </p>
                <CountDown type={countDownType.Week} />
              </div>
              {/* The order is the argument: when the next free lesson opens, the everyday
                  alternative, what the trial gives them, then the one button. */}
              <div className='w-col-12 story-finished-trial'>
                <p className='story-finished-trial-lead'>{t('finished.wantDaily')}</p>
                {isTrialEligible ? (
                  <>
                    <TrialPlanChoice />
                    <p className='story-finished-trial-note'>{t('finished.cancelNote')}</p>
                    <a href={PlanChangeLink} className='story-finished-trial-browse'>
                      {t('finished.browsePlans')}
                    </a>
                  </>
                ) : (
                  <div className='story-finished-trial-card'>
                    <p className='story-finished-trial-title'>{t('finished.paidIncludes')}</p>
                    <ul className='story-finished-trial-list'>
                      <li>{t('finished.paidDaily')}</li>
                      <li>{t('finished.paidLesson')}</li>
                      <li>{t('finished.paidTeacher')}</li>
                    </ul>
                    <p className='story-finished-trial-note'>{t('common:teacherLanguageNotice')}</p>
                    <a
                      href={PlanChangeLink}
                      className='button-4 w-inline-block button-normal story-finished-trial-cta'
                      style={{ cursor: 'pointer' }}
                    >
                      {t('finished.seePlans')}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  credentials: state.user.credentials,
  membership: state.user.membership,
  trialEligibility: state.user.trialEligibility,
  story: state.story.currentStory,
})
const mapActionsToProps = {}
export default connect(mapStateToProps, mapActionsToProps)(StoryFinished)
